"use client";

import {
  createOnlineChatSession,
  getOnlineChatSession,
  IOnlineChatMessage,
  IOnlineChatSession,
  sendOnlineChatMessage,
} from "@/services/onlineChat.service";
import { useSession } from "next-auth/react";
import { FormEvent, useEffect, useRef, useState } from "react";

const STORAGE_KEY_PREFIX = "novin_online_chat_session";

type StoredSession = {
  id: number;
  token: string;
};

const fallbackError =
  "ارتباط با پشتیبانی هوشمند برقرار نشد. لطفاً دوباره تلاش کنید.";

export default function FloatingOnlineChat() {
  const { data: session, status } = useSession();
  const [isOpen, setIsOpen] = useState(false);
  const [mobile, setMobile] = useState("");
  const [guestReady, setGuestReady] = useState(false);
  const [chatSession, setChatSession] = useState<IOnlineChatSession | null>(
    null,
  );
  const [messages, setMessages] = useState<IOnlineChatMessage[]>([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const isLoggedIn =
    status === "authenticated" && Boolean(session?.user?.token);
  const needsMobile = !isLoggedIn && !guestReady && !chatSession;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const restoreSession = async () => {
      const stored = getStoredSession();

      if (!stored) return;

      try {
        setIsLoading(true);
        const response = await getOnlineChatSession({
          id: stored.id,
          sessionToken: stored.token,
        });
        if (!response.data) {
          throw new Error(fallbackError);
        }
        setChatSession(response.data);
        setMessages(response.data.messages || []);
      } catch {
        localStorage.removeItem(getStorageKey());
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isLoggedIn]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    if (!isOpen || !chatSession) {
      return;
    }

    const refreshMessages = async () => {
      try {
        const response = await getOnlineChatSession({
          id: chatSession.id,
          sessionToken: chatSession.token,
        });
        if (response.data) {
          setChatSession(response.data);
          setMessages(response.data.messages || []);
        }
      } catch {
        // Temporary request failures must not interrupt the user's chat.
      }
    };

    const interval = window.setInterval(refreshMessages, 8000);
    return () => window.clearInterval(interval);
  }, [isOpen, chatSession?.id, chatSession?.token]);

  const getStorageKey = (guestMobile?: string | null): string => {
    const user = session?.user as any;

    if (isLoggedIn) {
      return `${STORAGE_KEY_PREFIX}:user:${user?.id || user?.mobile || user?.email || "current"}`;
    }

    return `${STORAGE_KEY_PREFIX}:guest:${guestMobile || mobile || "anonymous"}`;
  };

  const getCurrentPageUrl = () => {
    if (typeof window === "undefined") {
      return undefined;
    }

    return `${window.location.pathname}${window.location.search}`;
  };

  const getStoredSession = (): StoredSession | null => {
    if (typeof window === "undefined") {
      return null;
    }

    try {
      const value = localStorage.getItem(getStorageKey());
      return value ? (JSON.parse(value) as StoredSession) : null;
    } catch {
      return null;
    }
  };

  const saveSession = (nextSession: IOnlineChatSession) => {
    setChatSession(nextSession);
    setMessages(nextSession.messages || []);
    localStorage.setItem(
      getStorageKey(nextSession.mobile),
      JSON.stringify({
        id: nextSession.id,
        token: nextSession.token,
      }),
    );
  };

  const submitMobile = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!/^09\d{9}$/.test(mobile)) {
      setError("شماره موبایل معتبر وارد کنید.");
      return;
    }

    setGuestReady(true);
  };

  const submitMessage = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmed = message.trim();
    if (!trimmed) {
      return;
    }

    const optimisticMessage: IOnlineChatMessage = {
      id: Date.now(),
      sender_type: "user",
      message: trimmed,
      created_at: "",
    };

    setMessage("");
    setError("");
    setMessages((current) => [...current, optimisticMessage]);
    setIsTyping(true);

    try {
      let activeSession = chatSession;
      if (!activeSession) {
        const response = await createOnlineChatSession({
          mobile: isLoggedIn ? undefined : mobile,
          token: session?.user?.token,
          pageUrl: getCurrentPageUrl(),
        });
        if (!response.data) {
          throw new Error(fallbackError);
        }
        activeSession = response.data;
        saveSession(activeSession);
      }

      const response = await sendOnlineChatMessage({
        id: activeSession.id,
        sessionToken: activeSession.token,
        message: trimmed,
        pageUrl: getCurrentPageUrl(),
      });
      if (!response.data) {
        throw new Error(fallbackError);
      }
      setChatSession(response.data.session);
      setMessages(response.data.session.messages || []);
    } catch (err: any) {
      setError(err?.message || fallbackError);
      setMessages((current) =>
        current.filter((item) => item.id !== optimisticMessage.id),
      );
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div
      className="font-yekan fixed right-4 bottom-24 z-[90] sm:right-6 sm:bottom-5"
      dir="rtl"
    >
      {isOpen && (
        <section className="mb-4 flex h-[620px] max-h-[calc(100vh-120px)] w-[min(420px,calc(100vw-32px))] flex-col overflow-hidden rounded-[28px] border border-white/70 bg-white shadow-[0_28px_90px_rgba(42,38,97,0.24)]">
          <header className="relative overflow-hidden bg-gradient-to-br from-[#31237b] via-[#5c49d8] to-[#65d9d2] p-5 text-white">
            <button
              aria-label="بستن چت"
              className="absolute top-4 left-4 grid size-8 place-items-center rounded-full bg-white/15 text-lg"
              type="button"
              onClick={() => setIsOpen(false)}
            >
              ×
            </button>
            <p className="text-xs text-white/75">پشتیبانی هوشمند نوین وان</p>
            <h3 className="mt-2 text-xl font-extrabold">
              چطور می‌توانم کمک کنم؟
            </h3>
          </header>

          {needsMobile ? (
            <form
              className="flex flex-1 flex-col justify-center gap-4 p-5"
              onSubmit={submitMobile}
            >
              <div>
                <h4 className="text-lg font-bold text-slate-900">شروع گفتگو</h4>
                <p className="mt-2 text-sm leading-7 text-slate-500">
                  برای کاربران مهمان، شماره موبایل فقط برای نگهداری تاریخچه همین
                  گفتگو دریافت می‌شود.
                </p>
              </div>
              <input
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-right transition outline-none focus:border-[#5c49d8] focus:bg-white"
                inputMode="numeric"
                maxLength={11}
                placeholder="شماره موبایل مثل 09123456789"
                value={mobile}
                onChange={(event) =>
                  setMobile(event.target.value.replace(/\D/g, ""))
                }
              />
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button
                className="rounded-2xl bg-[#5c49d8] px-5 py-3 text-sm font-bold text-white shadow-lg shadow-[#5c49d8]/25 disabled:opacity-60"
                disabled={isLoading}
                type="submit"
              >
                {isLoading ? "در حال ایجاد گفتگو..." : "شروع چت"}
              </button>
            </form>
          ) : (
            <>
              <div className="flex-1 space-y-3 overflow-y-auto bg-[#f7f8fc] p-4">
                {isLoading && (
                  <p className="text-center text-sm text-slate-500">
                    در حال بارگذاری گفتگو...
                  </p>
                )}
                {!isLoading && messages.length === 0 && (
                  <div className="rounded-2xl bg-white p-4 text-sm leading-7 text-slate-600 shadow-sm">
                    سلام، من دستیار هوشمند نوین وان هستم. سؤال خود را درباره
                    خدمات سایت بپرسید.
                  </div>
                )}
                {messages.map((item) => (
                  <div
                    key={`${item.id}-${item.created_at}`}
                    className={`flex ${item.sender_type === "user" ? "justify-start" : "justify-end"}`}
                  >
                    <p
                      className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm leading-7 whitespace-pre-line ${
                        item.sender_type === "user"
                          ? "rounded-br-sm bg-[#5c49d8] text-white"
                          : "rounded-bl-sm bg-white text-slate-700 shadow-sm"
                      }`}
                    >
                      {item.message}
                    </p>
                  </div>
                ))}
                {isTyping && (
                  <div className="flex justify-end">
                    <div className="rounded-2xl rounded-bl-sm bg-white px-4 py-3 text-sm text-slate-500 shadow-sm">
                      در حال پاسخ‌گویی...
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {error && (
                <p className="border-t border-red-100 bg-red-50 px-4 py-2 text-xs text-red-600">
                  {error}
                </p>
              )}

              <form
                className="flex gap-2 border-t border-slate-100 bg-white p-3"
                onSubmit={submitMessage}
              >
                <input
                  className="min-w-0 flex-1 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#5c49d8] focus:bg-white"
                  placeholder="پیام خود را بنویسید..."
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                />
                <button
                  className="rounded-2xl bg-[#5c49d8] px-4 py-3 text-sm font-bold text-white disabled:opacity-60"
                  disabled={isTyping || !message.trim()}
                  type="submit"
                >
                  ارسال
                </button>
              </form>
            </>
          )}
        </section>
      )}

      {!isOpen && (
        <button
          aria-label="باز کردن چت آنلاین"
          className="group flex items-center gap-2 rounded-full border-2 border-[#65d9d2] bg-white px-3 py-2 text-xs font-bold text-[#31237b] shadow-[0_18px_45px_rgba(49,35,123,0.22)] transition hover:-translate-y-1 hover:border-white hover:bg-[#65d9d2] hover:text-[#1f1b5f] sm:gap-3 sm:px-5 sm:py-3 sm:text-sm"
          type="button"
          onClick={() => setIsOpen(true)}
        >
          <span className="grid size-8 place-items-center rounded-full bg-[#5c49d8] text-base text-white shadow-inner shadow-white/20 transition group-hover:bg-[#31237b] sm:size-10 sm:text-xl">
            ؟
          </span>
          <span>چت آنلاین</span>
        </button>
      )}
    </div>
  );
}
